import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Transaction {
  id: number;
  reference?: string;
  project_id: string;
  contact_id: number;
  user_id: number;

  // Pipeline / suivi
  phase_transaction?: string;
  date_creation_transaction?: string;
  date_effective?: string;
  date_contact?: string;
  date_envoi_devis?: string;
  date_fin_pret?: string;
  date_derniere_demande_information?: string;
  revenu?: number;
  commentaire?: string;
  resultat_appel?: string;
  status?: string;
  date_traitement?: string;

  // Devis / contrat
  nbre_devis_envoyes?: number;
  devis_envoye?: boolean;
  nom_assureurs?: string;
  numero_devis?: string;
  numero_contrat?: string;
  statut_contrat?: string;
  nb_contrat?: number;
  motif_refus?: string;
  /** Branche de l'affaire (EMPRUNTEUR, SANTE PRO, PER…). Affichage seul :
   *  posée à la création par le flux d'origine, jamais saisie dans l'UI.
   *  À ne pas confondre avec `produit`, le produit du contrat souscrit. */
  famille_produit?: string;
  assureur?: string;
  produit?: string;
  nom_produit?: string;
  cout_mensuel_assurance?: number;
  cout_total_assurance?: number;
  precedent_assureur?: string;

  // Prêt
  type_pret?: string;
  objet_du_pret?: string;
  type_reprise?: string;
  montant_credit?: number;
  taux?: number;
  duree?: number;

  // Risque Assuré 1
  deplacement_professionnel?: string;
  port_charge?: boolean;
  travaille_en_hauteur?: string;
  nb_enfant_assure?: number;
  total_encours?: number;
  quotite?: number;

  // Risque Assuré 2
  assure2_quotite?: number;
  assure2_deplacement_professionnel?: string;
  assure2_port_charge?: boolean;
  assure2_travaille_en_hauteur?: string;

  // Garanties / technique
  banque?: string;
  nom_projet?: string;
  prets_additionnels?: any;

  // Résultats comparateur
  tarif_mensuel_moins_cher?: number;
  cotisation_totale_moins_chere?: number;
  assureur_moins_cher?: string;

  // Métadonnées
  origine?: string;
  projet_source_id?: string;

  // Contact lié (embarqué pour l'affichage liste)
  contact?: {
    id: number;
    reference?: string;
    civilite?: string;
    nom?: string;
    prenom?: string;
    mobile?: string;
    email?: string;
    ville?: string;
    /** Statut de lead — même référentiel qu'en historique projets et agenda. */
    statut_du_lead?: string | null;
  } | null;

  [key: string]: any;
}

export interface TransactionFilters {
  contact_id?: number;
  phase_transaction?: string;
  /** Recherche texte : client (nom/prénom/mobile/email), référence, banque, assureur… */
  search?: string;
  /** Transaction d'un projet CAC précis (ouverture depuis l'historique projets). */
  project_id?: string;
  origine?: string;
  user_id?: number;
  start_date?: string;
  end_date?: string;
}

@Injectable({ providedIn: 'root' })
export class TransactionService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/transactions`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  /** Paramètres de filtrage, communs à `list` et `count` (mêmes filtres des deux
   *  côtés, sinon la pagination annonce un total qui ne colle pas aux lignes). */
  private buildFilterParams(filters?: TransactionFilters): HttpParams {
    let params = new HttpParams();
    if (filters?.contact_id != null) {
      params = params.set('contact_id', filters.contact_id.toString());
    }
    if (filters?.phase_transaction) {
      params = params.set('phase_transaction', filters.phase_transaction);
    }
    if (filters?.search) {
      params = params.set('search', filters.search);
    }
    if (filters?.project_id) {
      params = params.set('project_id', filters.project_id);
    }
    if (filters?.origine) {
      params = params.set('origine', filters.origine);
    }
    if (filters?.user_id != null) {
      params = params.set('user_id', filters.user_id.toString());
    }
    if (filters?.start_date) {
      params = params.set('start_date', filters.start_date);
    }
    if (filters?.end_date) {
      params = params.set('end_date', filters.end_date);
    }
    return params;
  }

  list(limit = 100, offset = 0, filters?: TransactionFilters): Observable<Transaction[]> {
    const params = this.buildFilterParams(filters)
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.http.get<Transaction[]>(this.apiUrl, {
      params,
      headers: this.getAuthHeaders(),
    });
  }

  /** Commentaires modifiés dans la session : les tables ouvertes s'alignent. */
  private commentaireChangedSubject = new Subject<{ project_id: string; commentaire: string | null }>();
  readonly commentaireChanged$ = this.commentaireChangedSubject.asObservable();

  /** Édite le commentaire par `project_id` : source unique partagée par la
   *  table transactions, l'historique projets et l'agenda. */
  updateCommentaireByProject(projectId: string, commentaire: string | null): Observable<{ project_id: string; commentaire: string | null }> {
    return this.http.patch<{ project_id: string; commentaire: string | null }>(
      `${this.apiUrl}/by-project/${encodeURIComponent(projectId)}/commentaire`,
      { commentaire },
      { headers: this.getAuthHeaders() },
    ).pipe(tap(res => this.commentaireChangedSubject.next(res)));
  }

  /** Édite manuellement le résultat d'appel par `project_id` (OK | NRP | null =
   *  « Nouveau ») : source unique Transaction.resultat_appel, partagée par la
   *  table transactions, l'historique projets et l'agenda. */
  updateResultatAppelByProject(projectId: string, resultatAppel: string | null): Observable<{ project_id: string; resultat_appel: string | null }> {
    return this.http.patch<{ project_id: string; resultat_appel: string | null }>(
      `${this.apiUrl}/by-project/${encodeURIComponent(projectId)}/resultat-appel`,
      { resultat_appel: resultatAppel },
      { headers: this.getAuthHeaders() },
    );
  }

  count(filters?: TransactionFilters): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.apiUrl}/count`, {
      params: this.buildFilterParams(filters),
      headers: this.getAuthHeaders(),
    });
  }

  /** `project_id` de TOUTES les transactions correspondant aux filtres.
   *  La liste étant paginée côté serveur, c'est le seul moyen d'agir sur
   *  l'ensemble du résultat (export) et pas seulement sur la page affichée. */
  projectIds(filters?: TransactionFilters): Observable<{ project_ids: string[] }> {
    return this.http.get<{ project_ids: string[] }>(`${this.apiUrl}/project-ids`, {
      params: this.buildFilterParams(filters),
      headers: this.getAuthHeaders(),
    });
  }

  /** Payload du formulaire CAC reconstruit depuis Contact+Transaction (sans project_history). */
  getFormPayload(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}/form`, {
      headers: this.getAuthHeaders(),
    });
  }

  get(id: number): Observable<Transaction> {
    return this.http.get<Transaction>(`${this.apiUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  create(payload: Partial<Transaction>): Observable<Transaction> {
    return this.http.post<Transaction>(this.apiUrl, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  update(id: number, payload: Partial<Transaction>): Observable<Transaction> {
    return this.http.patch<Transaction>(`${this.apiUrl}/${id}`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }
}
